import type { MockRaceResult, Style } from './types';

function hashSeed(seed: string): number {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return (h >>> 0) / 4294967295;
}

function fmtTime(ms: number) {
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    const cs = Math.floor((ms % 1000) / 10);
    return m > 0
        ? `${m}:${String(s).padStart(2, '0')}.${String(cs).padStart(2, '0')}`
        : `${s}.${String(cs).padStart(2, '0')}`;
}

function makeSplits(totalMs: number, count: number, seedBase: string): number[] {
    const base = totalMs / count;
    return Array.from({ length: count }, (_, i) => {
        const drift = 1 + i * 0.006 + (hashSeed(seedBase + i) - 0.5) * 0.01;
        return Math.round(base * drift);
    });
}

interface EventTmpl {
    d: number;
    s: Style;
    baseMs: number;
    splits: number;
}
const TEMPLATES: EventTmpl[] = [
    { d: 50, s: 'SL', baseMs: 28_800, splits: 1 },
    { d: 100, s: 'SL', baseMs: 61_500, splits: 2 },
    { d: 200, s: 'SL', baseMs: 131_000, splits: 4 },
    { d: 400, s: 'SL', baseMs: 272_000, splits: 8 },
    { d: 100, s: 'DO', baseMs: 69_000, splits: 2 },
    { d: 50, s: 'FA', baseMs: 30_500, splits: 1 },
    { d: 100, s: 'RA', baseMs: 77_000, splits: 2 },
    { d: 200, s: 'MX', baseMs: 143_000, splits: 4 }
];

const CATEGORY_FACTOR: Record<string, number> = {
    R: 1.12,
    C: 1.06,
    J: 1.02,
    S: 1.0,
    A: 0.98
};
const GENDER_FACTOR = { M: 1.0, F: 1.09 };

const EVENT_NAMES = [
    'Campionato Regionale Lazio',
    'Meeting Città di Roma',
    'Trofeo Primavera',
    'Campionati Italiani Assoluti',
    'Criteria Nazionali',
    'Coppa Italia',
    'Meeting del Tiberino',
    'Trofeo Rudelli'
];

export function generateRaceResultsForAthlete(
    athleteId: string,
    category: string,
    gender: 'M' | 'F'
): MockRaceResult[] {
    const catFactor = CATEGORY_FACTOR[category] ?? 1.04;
    const gFactor = GENDER_FACTOR[gender];
    const out: MockRaceResult[] = [];

    // Numero gare: 10-25 nei 24 mesi
    const seedN = hashSeed(athleteId + 'rrn');
    const nRaces = 10 + Math.floor(seedN * 16);

    for (let i = 0; i < nRaces; i++) {
        const seedI = athleteId + 'rr' + i;
        const tmplIdx = Math.floor(hashSeed(seedI + 't') * TEMPLATES.length);
        const t = TEMPLATES[tmplIdx];
        const perfVar = 0.95 + hashSeed(seedI + 'p') * 0.12; // ±6%
        const timeMs = Math.round(t.baseMs * catFactor * gFactor * perfVar);
        const splits = makeSplits(timeMs, t.splits, seedI);

        const monthsAgo = Math.floor(hashSeed(seedI + 'm') * 24);
        const daysAgo = monthsAgo * 30 + Math.floor(hashSeed(seedI + 'd') * 28);
        const d = new Date('2026-04-22');
        d.setDate(d.getDate() - daysAgo);
        const dateStr = d.toISOString().slice(0, 10);

        const finaPoints = Math.round(500 + (1 - perfVar) * 400 + hashSeed(seedI + 'f') * 80);
        const position = 1 + Math.floor(hashSeed(seedI + 'pos') * 12);
        const isPB = hashSeed(seedI + 'pb') < 0.18;

        out.push({
            id: `rr-${athleteId}-${i}`,
            athleteId,
            eventId: `evt-${(i % 6) + 1}`,
            eventName: EVENT_NAMES[i % EVENT_NAMES.length],
            date: dateStr,
            distance: t.d,
            style: t.s,
            pool: i % 3 === 0 ? '25' : '50',
            timeMs,
            time: fmtTime(timeMs),
            finaPoints: Math.max(300, Math.min(950, finaPoints)),
            position,
            isPB,
            splits,
            passaggi: splits.map((ms, idx) => ({
                dist: (t.d * (idx + 1)) / t.splits,
                time: fmtTime(ms)
            })),
            category
        });
    }

    // Ordina per data desc
    return out.sort((a, b) => b.date.localeCompare(a.date));
}

export const mockRaceResults = generateRaceResultsForAthlete('athlete-self', 'C', 'F');

export function raceResultsByAthlete(athleteId: string, category = 'C', gender: 'M' | 'F' = 'F'): MockRaceResult[] {
    return generateRaceResultsForAthlete(athleteId, category, gender);
}
